import { useState, useEffect } from 'react';
import api from '../api';
import { fmt, fmtDollars, fmtPct } from '../format';
import HelpLink from '../components/HelpLink';
import useTableControls from '../hooks/useTableControls';

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [byPm, setByPm] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getDashboardSummary(), api.getDashboardByPm()])
      .then(([s, pm]) => { setSummary(s); setByPm(pm); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loading">Loading...</div>;

  const refreshed = summary?.data_refreshed_at
    ? new Date(summary.data_refreshed_at).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'Unknown';

  return (
    <div className="page dashboard-page">
      <div className="page-header">
        <div>
          <h1>Executive Summary</h1>
          <p className="page-subtitle">Company-Wide Work in Progress &mdash; FY {summary?.fiscal_year}</p>
        </div>
        <div className="refresh-badge">Data as of {refreshed}</div>
      </div>

      <div className="kpi-grid">
        <KpiCard label="Active Projects" value={summary?.active_projects} format="int" />
        <KpiCard label="Total Contract Revenue" value={summary?.total_contract_revenue} format="dollar" />
        <KpiCard label="Earned Revenue to Date" value={summary?.total_earned_revenue} format="dollar" />
        <KpiCard label="Contract Costs to Date" value={summary?.total_costs_to_date} format="dollar" />
        <KpiCard label="Gross Profit" value={summary?.total_estimated_gp} format="dollar" accent helpId="col-c" />
        <KpiCard label="GP Margin" value={summary?.overall_gp_pct} format="pct" accent helpId="gp-pct" />
        <KpiCard label="Backlog" value={summary?.total_backlog} format="dollar" helpId="backlog" />
        <KpiCard label="Retainage Held" value={summary?.total_retainage} format="dollar" helpId="retainage" />
      </div>

      <div className="wip-position-summary dashboard-position">
        <div className="position-card underbilled">
          <div className="position-label">Costs and Estimated Earnings in Excess of Billings <HelpLink id="col-j" /></div>
          <div className="position-value">{fmtDollars(summary?.total_underbilled)}</div>
        </div>
        <div className="position-card overbilled">
          <div className="position-label">Billings in Excess of Costs and Estimated Earnings <HelpLink id="col-k" /></div>
          <div className="position-value">{fmtDollars(summary?.total_overbilled)}</div>
        </div>
        <div className="position-card net">
          <div className="position-label">Net WIP Position <HelpLink id="net-wip" /></div>
          <div className="position-value">{fmtDollars((summary?.total_underbilled || 0) - (summary?.total_overbilled || 0))}</div>
        </div>
      </div>

      <PmTable data={byPm} />
    </div>
  );
}

function PmTable({ data }) {
  const { sorted, toggleSort, sortIcon } = useTableControls(data, { key: 'total_revenue', dir: 'desc' });
  return (
    <div className="section">
      <h2>Performance by Project Manager</h2>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="col-text sortable" onClick={() => toggleSort('project_manager')}>Project Manager{sortIcon('project_manager')}</th>
              <th className="col-int sortable" onClick={() => toggleSort('project_count')}>Projects{sortIcon('project_count')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('total_revenue')}>Contract Revenue{sortIcon('total_revenue')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('total_costs')}>Costs to Date{sortIcon('total_costs')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('total_gp')}>Gross Profit{sortIcon('total_gp')}</th>
              <th className="col-pct sortable" onClick={() => toggleSort('gp_pct')}>GP %{sortIcon('gp_pct')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('total_underbilled')}>Under-Billed{sortIcon('total_underbilled')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('total_overbilled')}>Over-Billed{sortIcon('total_overbilled')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((pm, i) => (
              <tr key={i}>
                <td className="col-text">{pm.project_manager || 'Unassigned'}</td>
                <td className="col-int">{pm.project_count}</td>
                <td className="col-money">{fmt(pm.total_revenue)}</td>
                <td className="col-money">{fmt(pm.total_costs)}</td>
                <td className={`col-money ${pm.total_gp < 0 ? 'negative' : ''}`}>{fmt(pm.total_gp)}</td>
                <td className="col-pct">{fmtPct(pm.gp_pct)}</td>
                <td className="col-money">{fmt(pm.total_underbilled)}</td>
                <td className="col-money">{fmt(pm.total_overbilled)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({ label, value, format, accent, helpId }) {
  let display;
  if (format === 'int') display = (value ?? 0).toLocaleString();
  else if (format === 'pct') display = fmtPct(value);
  else if (format === 'dollar') display = fmtDollars(value);
  else display = fmt(value);

  return (
    <div className={`kpi-card ${accent ? 'kpi-accent' : ''}`}>
      <div className="kpi-label">{label}{helpId && <HelpLink id={helpId} />}</div>
      <div className="kpi-value">{display}</div>
    </div>
  );
}
